import React, {
  useState,
  FC,
  useRef,
  ReactNode,
  isValidElement,
  FocusEvent,
  KeyboardEvent
} from 'react';
import classNames from 'classnames/bind';

import styles from './index.pcss';
import Icon from '../../../../../atoms/Icon';

type IconType = React.ReactNode | string;

export type variant = 'small' | 'big';

export interface BtnProps {
  id?: string | undefined;
  htmlType?: 'button' | 'submit' | 'reset' | undefined;
  variant?: variant;
  size?: 'xs' | 'sm' | 'lg' | 'md';
  fontWeight?: 'regular' | 'bold';
  /**
   * `xxs`: 8px
   * `xs`: 10px
   * `sm`: 12px
   * `reg`: 14px
   * `md`: 16px
   * `lg`: 18px
   */
  fontSize?: 'xxs' | 'xs' | 'sm' | 'reg' | 'md' | 'lg';
  btnLabel?: string;
  disabled?: boolean;
  btnTransParent?: boolean;
  loading?: boolean;
  emphasis?: boolean;
  fullWidth?: boolean;
  ariaLabel?: string;
  iconClass?: string;
  textClass?: string;
  href?: string;
  target?: string;
  /** makes circular button translucent -> opacity: 0.7 */
  translucent?: boolean;
  pressed?: boolean;
  diameter?: number;
  radius?: number;
  icon?: IconType;
  iconStyles?: import('../../../../../atoms/Icon/Icon').IconStyle;
  /** if button controls visibility of other element, pass visible or not */
  ariaExpanded?: boolean;
  /** if button controls visibility of other element, pass element's id */
  ariaControlsId?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLAnchorElement>) => void;
}
const cx = classNames.bind(styles);

const ActionButton: FC<BtnProps> = (props: BtnProps) => {
  const {
    id,
    btnLabel,
    htmlType = 'button',
    ariaLabel,
    onClick,
    onFocus,
    onBlur,
    onKeyDown,
    disabled,
    variant = '',
    icon = '',
    size = 'lg',
    emphasis,
    fullWidth,
    fontWeight = 'bold',
    iconStyles,
    ariaExpanded,
    ariaControlsId,
    fontSize,
    btnTransParent,
    iconClass,
    textClass
  } = props;

  const [focus, setFocus] = useState<boolean>(false);

  const elementRef = useRef<HTMLButtonElement>(null);

  const handleClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void = event => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
  };

  const handleFocus: (event: FocusEvent<HTMLButtonElement>) => void = event => {
    event.stopPropagation();
    setFocus(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur: (event: FocusEvent<HTMLButtonElement>) => void = event => {
    event.stopPropagation();
    setFocus(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleKeyDown: (event: KeyboardEvent<HTMLAnchorElement>) => void = event => {
    event.stopPropagation();
    if (onKeyDown) {
      onKeyDown(event);
    } else if (!disabled && focus && ['Enter', ' ', 'Spacebar'].some(key => key === event.key)) {
      elementRef?.current?.click();
    }
  };

  const iconRenderer: (iconEle: IconType) => ReactNode = iconEle => {
    if (iconEle) {
      if (isValidElement(iconEle)) {
        return iconEle;
      }
      const stylesObj: import('../../../../../atoms/Icon/Icon').IconStyle = iconStyles || {
        color: 'black',
        fontWeight
      };
      return <Icon icon={iconEle} styles={stylesObj} iconClass={iconClass} />;
    }
    return null;
  };
  const classes = cx('btn', {
    [`${variant}`]: true,
    [`${size}`]: variant === 'big',
    'btn-lg': variant === 'big',
    'btn-sm': variant === 'small',
    'full-width': fullWidth,
    'is-uppercase': emphasis,
    'btn-backg-transparent': btnTransParent,
    'btn-center': !icon,
    [`font-${fontSize}`]: !!fontSize
  });

  return (
    <button
      id={id}
      className={classes}
      ref={elementRef}
      onClick={handleClick}
      type={htmlType}
      onFocus={handleFocus}
      onBlur={handleBlur}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControlsId}
    >
      {btnLabel ? <span className={textClass}>{btnLabel}</span> : null}
      {iconRenderer(icon)}
    </button>
  );
};

ActionButton.defaultProps = {
  htmlType: 'button',
  variant: 'big',
  size: 'lg',
  disabled: false,
  loading: false,
  emphasis: false,
  fullWidth: false,
  fontWeight: 'bold',
  translucent: false,
  btnTransParent: false,
  diameter: 40
};

export default ActionButton;
